import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Phone, Mail, Calendar as CalendarIcon, MapPin, MessageCircle, Loader2, Copy, ArrowRight, Sparkles, ListChecks, Lightbulb } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * PrepareActionSheet — sheet bottom-up qui appelle /api/ai/prepare-action
 * et affiche un script/email/brief prêt à l'emploi.
 *
 * Props :
 *   open: bool
 *   onOpenChange(v: bool)
 *   prospectId: string
 *   prospect: object (used for title / preview)
 *   actionType: 'call' | 'email' | 'rdv' | 'visit' | 'linkedin' | 'auto'
 */
const ICONS = {
  call: Phone,
  email: Mail,
  rdv: CalendarIcon,
  visit: MapPin,
  linkedin: MessageCircle,
};
const TITLES = {
  call: "Préparer l'appel",
  email: "Brouillon d'email",
  rdv: "Préparer le RDV",
  visit: "Brief de visite",
  linkedin: "Message LinkedIn",
};

function CopyButton({ text, label = "Copier", testid, onLog }) {
  const copy = () => {
    if (!text) return;
    try {
      navigator.clipboard.writeText(text);
      toast.success("Copié dans le presse-papier");
      if (onLog) onLog();
    } catch {
      toast.error("Copie indisponible");
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-300 hover:text-emerald-200 transition-colors px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30"
    >
      <Copy className="w-3 h-3" /> {label}
    </button>
  );
}

/** Log the prepared action as an interaction on the prospect. Idempotent guard
 *  per (prospect, type) — only fires once per sheet opening. */
function useInteractionLogger(prospectId, prospect) {
  const [logged, setLogged] = useState(false);
  return (type, summary) => {
    if (!prospectId || logged) return;
    setLogged(true);
    api.post("/interactions", {
      prospect_id: prospectId,
      type, // 'call' | 'email' | 'visit' | 'linkedin' | etc.
      duration: 0,
      outcome: "Préparé par Prosperr",
      notes: summary || "",
      ai_analysis: "Action préparée via assistant IA",
    }).catch(() => {});
    toast.success("Action enregistrée sur la fiche");
  };
}

export default function PrepareActionSheet({ open, onOpenChange, prospectId, prospect, actionType = "auto" }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !prospectId) return;
    setLoading(true);
    setData(null);
    setError(null);
    api.post("/ai/prepare-action", { prospect_id: prospectId, action_type: actionType })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Service IA indisponible"))
      .finally(() => setLoading(false));
  }, [open, prospectId, actionType]);

  const type = data?.action_type || actionType;
  const Icon = ICONS[type] || Phone;
  const title = TITLES[type] || "Préparation";
  const logInteraction = useInteractionLogger(prospectId, prospect);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl bg-[var(--surface)] border-t border-[var(--border)] text-[var(--text)] max-h-[90vh] overflow-hidden flex flex-col"
      >
        <SheetHeader className="pb-2 shrink-0">
          <SheetTitle className="text-left text-[var(--text)] flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/40 grid place-items-center">
              <Icon className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2} />
            </div>
            <span>{title}</span>
            <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-300 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Prosperr
            </span>
          </SheetTitle>
          {prospect && (
            <p className="text-[12px] text-zinc-400 mt-1 text-left">
              {prospect.name || prospect.company || "Prospect"} · {prospect.company || prospect.sector || ""}
            </p>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 pb-4 space-y-3">
          {loading && (
            <div className="grid place-items-center py-16">
              <div className="text-center">
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin mx-auto mb-3" />
                <div className="text-[12px] text-zinc-400">Prosperr prépare votre action…</div>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-10 text-[13px] text-rose-300">{error}</div>
          )}

          {!loading && data && type === "call" && <CallScript data={data} onLog={(s) => logInteraction("call", s)} />}
          {!loading && data && type === "email" && <EmailDraft data={data} prospect={prospect} onLog={(s) => logInteraction("email", s)} />}
          {!loading && data && (type === "rdv" || type === "visit") && <MeetingBrief data={data} onLog={(s) => logInteraction("visit", s)} />}
          {!loading && data && type === "linkedin" && <LinkedinDraft data={data} onLog={(s) => logInteraction("linkedin", s)} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Sub-views per action type ────────────────────────────── */

function Section({ title, children, icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)]/70 backdrop-blur p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && <Icon className="w-3 h-3 text-emerald-300" strokeWidth={2} />}
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-300">{title}</div>
      </div>
      {children}
    </div>
  );
}

function CallScript({ data, onLog }) {
  return (
    <>
      <Section title="Objectif" testid="call-objectif">
        <p className="text-[13.5px] text-zinc-100 leading-relaxed">{data.objectif}</p>
        {data.meilleur_moment && (
          <p className="text-[11.5px] text-zinc-400 mt-1.5">⏰ {data.meilleur_moment}</p>
        )}
      </Section>

      <Section title="Accroche" icon={Sparkles} testid="call-accroche">
        <p className="text-[13.5px] text-zinc-100 leading-relaxed">{data.accroche}</p>
        <div className="mt-2"><CopyButton text={data.accroche} testid="copy-accroche" onLog={() => onLog?.(data.objectif)} /></div>
      </Section>

      {Array.isArray(data.questions) && data.questions.length > 0 && (
        <Section title="Questions de qualification" icon={ListChecks} testid="call-questions">
          <ol className="space-y-1.5">
            {data.questions.map((q, i) => (
              <li key={i} className="text-[13px] text-zinc-100 leading-snug flex gap-2">
                <span className="text-emerald-300 font-semibold tabular-nums shrink-0">{i + 1}.</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {data.valeur && (
        <Section title="Proposition de valeur" testid="call-valeur">
          <p className="text-[13px] text-zinc-100 leading-relaxed">{data.valeur}</p>
        </Section>
      )}

      {Array.isArray(data.objections) && data.objections.length > 0 && (
        <Section title="Objections probables" testid="call-objections">
          <div className="space-y-2">
            {data.objections.map((o, i) => (
              <div key={i} className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-alt)]/50 p-2.5">
                <div className="text-[12px] font-medium text-rose-300">« {o.objection} »</div>
                <div className="text-[12px] text-zinc-300 mt-1 leading-snug">→ {o.reponse}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {data.closing && (
        <Section title="Closing" testid="call-closing">
          <p className="text-[13.5px] text-zinc-100 leading-relaxed">{data.closing}</p>
          <div className="mt-2"><CopyButton text={data.closing} testid="copy-closing" /></div>
        </Section>
      )}
    </>
  );
}

function EmailDraft({ data, prospect }) {
  const fullEmail = `${data.objet ? `Objet : ${data.objet}\n\n` : ""}${data.corps || ""}`;
  const recipient = prospect?.email || "";
  const mailto = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(data.objet || "")}&body=${encodeURIComponent(data.corps || "")}`;
  const gmail  = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(recipient)}&su=${encodeURIComponent(data.objet || "")}&body=${encodeURIComponent(data.corps || "")}`;
  return (
    <>
      {data.objet && (
        <Section title="Objet" testid="email-objet">
          <p className="text-[14px] font-semibold text-zinc-100 leading-snug">{data.objet}</p>
          <div className="mt-2"><CopyButton text={data.objet} testid="copy-objet" /></div>
        </Section>
      )}
      {data.corps && (
        <Section title="Corps du message" icon={Mail} testid="email-corps">
          <pre className="text-[13px] text-zinc-100 leading-relaxed whitespace-pre-wrap font-sans">{data.corps}</pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <CopyButton text={data.corps} testid="copy-corps" />
            <CopyButton text={fullEmail} label="Copier tout" testid="copy-fullemail" />
          </div>
        </Section>
      )}
      {/* CTA : ouvrir dans le client mail */}
      <div className="grid grid-cols-2 gap-2 mt-1" data-testid="email-send-actions">
        <a
          href={mailto}
          data-testid="email-mailto"
          className="hp-btn hp-btn-primary h-11 text-[13px] flex items-center justify-center gap-2"
          onClick={() => {
            if (!recipient) {
              // mailto still works without `to` — letting user pick
            }
          }}
        >
          <Mail className="w-3.5 h-3.5" /> Ouvrir dans Mail
        </a>
        <a
          href={gmail}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="email-gmail"
          className="hp-btn hp-btn-secondary h-11 text-[13px] flex items-center justify-center gap-2"
        >
          <Mail className="w-3.5 h-3.5" /> Ouvrir dans Gmail
        </a>
      </div>
      {!recipient && (
        <p className="text-[10.5px] text-amber-300/80 text-center px-2 leading-relaxed">
          Aucune adresse email enregistrée pour ce prospect — ajoutez-la sur sa fiche pour pré-remplir le destinataire.
        </p>
      )}
      {data.signature_hint && (
        <p className="text-[11px] text-zinc-500 px-2 leading-relaxed">{data.signature_hint}</p>
      )}
    </>
  );
}

function MeetingBrief({ data, onLog }) {
  return (
    <>
      {data.objectif && (
        <Section title="Objectif" testid="rdv-objectif">
          <p className="text-[13.5px] text-zinc-100 leading-relaxed">{data.objectif}</p>
        </Section>
      )}
      {Array.isArray(data.agenda_proposé || data.questions) && (data.agenda_proposé || data.questions).length > 0 && (
        <Section title={data.agenda_proposé ? "Agenda proposé" : "Questions clés"} icon={ListChecks} testid="rdv-agenda">
          <ol className="space-y-1.5">
            {(data.agenda_proposé || data.questions).map((q, i) => (
              <li key={i} className="text-[13px] text-zinc-100 leading-snug flex gap-2">
                <span className="text-emerald-300 font-semibold tabular-nums shrink-0">{i + 1}.</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}
      {Array.isArray(data.questions_cles) && data.questions_cles.length > 0 && (
        <Section title="Questions de qualification" icon={ListChecks} testid="rdv-questions-cles">
          <ol className="space-y-1.5">
            {data.questions_cles.map((q, i) => (
              <li key={i} className="text-[13px] text-zinc-100 leading-snug flex gap-2">
                <span className="text-emerald-300 font-semibold tabular-nums shrink-0">{i + 1}.</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}
      {data.valeur && (
        <Section title="Valeur à mettre en avant" icon={Sparkles} testid="rdv-valeur">
          <p className="text-[13px] text-zinc-100 leading-relaxed">{data.valeur}</p>
        </Section>
      )}
      {Array.isArray(data.materiel_à_apporter) && data.materiel_à_apporter.length > 0 && (
        <Section title="Matériel à apporter" testid="rdv-materiel">
          <ul className="space-y-1 list-disc list-inside text-[13px] text-zinc-100">
            {data.materiel_à_apporter.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </Section>
      )}
      {data.prochaine_etape && (
        <Section title="Prochaine étape" icon={ArrowRight} testid="rdv-next">
          <p className="text-[13px] text-zinc-100 leading-relaxed">{data.prochaine_etape}</p>
        </Section>
      )}
    </>
  );
}

function LinkedinDraft({ data, onLog }) {
  return (
    <>
      {data.connection && (
        <Section title="Demande de connexion" icon={MessageCircle} testid="li-connection">
          <p className="text-[13px] text-zinc-100 leading-relaxed">{data.connection}</p>
          <div className="mt-2 flex items-center gap-2">
            <CopyButton text={data.connection} testid="copy-connection" onLog={() => onLog?.("Demande LinkedIn")} />
            <span className="text-[10px] text-zinc-500 tabular-nums">{(data.connection || "").length}/290</span>
          </div>
        </Section>
      )}
      {data.first && (
        <Section title="Premier message" icon={Lightbulb} testid="li-first">
          <pre className="text-[13px] text-zinc-100 leading-relaxed whitespace-pre-wrap font-sans">{data.first}</pre>
          <div className="mt-2"><CopyButton text={data.first} testid="copy-first" onLog={() => onLog?.("Premier message LinkedIn")} /></div>
        </Section>
      )}
    </>
  );
}
